import { viewerStrings } from '../copy/strings.js';
import { hasText, optionalText } from '../copy/text.js';
import { useState, useSyncExternalStore } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Checkbox from '@cloudscape-design/components/checkbox';
import type { FieldState } from '../state/field-state.js';

export function FieldsDialog({
  state,
  open: controlledOpen,
  onOpenChange,
  trigger = 'plain',
}: {
  state: FieldState;
  /** Controlled visibility; omit to let the trigger manage it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `plain` is the navigation chip, `columns` the prominent toolbar button, `none` no trigger. */
  trigger?: 'plain' | 'columns' | 'none';
}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState('');
  const snapshot = useSyncExternalStore(state.subscribe, state.getSnapshot);
  const selected = new Set(snapshot.preference.visible);
  return (
    <>
      {trigger === 'plain' && hasText(viewerStrings.webComponentsFieldsDialog.fields) ? (
        <button type="button" onClick={() => setOpen(true)}>
          {viewerStrings.webComponentsFieldsDialog.fields}
        </button>
      ) : null}
      {trigger === 'columns' && hasText(viewerStrings.webComponentsFieldsDialog.showHideColumns) ? (
        <button type="button" className="columns-button" onClick={() => setOpen(true)}>
          {viewerStrings.webComponentsFieldsDialog.showHideColumns}
        </button>
      ) : null}
      <Modal
        visible={open}
        onDismiss={() => setOpen(false)}
        header={optionalText(viewerStrings.webComponentsFieldsDialog.fields2)}
        size="medium"
      >
        <div className="field-tools" data-copy-group>
          <select
            aria-label={optionalText(viewerStrings.webComponentsFieldsDialog.fieldPreset)}
            value={snapshot.preference.presetId}
            onChange={(event) => state.applyPreset(event.target.value)}
          >
            {hasText(viewerStrings.webComponentsFieldsDialog.customSelection) ? (
              <option value="custom" disabled>
                {viewerStrings.webComponentsFieldsDialog.customSelection}
              </option>
            ) : null}
            {[...state.capabilities.manifest.presets]
              .sort((a, b) =>
                a.title.localeCompare(b.title, 'en', { sensitivity: 'base', numeric: true }),
              )
              .map((preset) => (
                <option key={preset.id} value={preset.id}>
                  {preset.title}
                </option>
              ))}
          </select>
          <input
            type="search"
            placeholder={optionalText(viewerStrings.webComponentsFieldsDialog.findAField)}
            aria-label={optionalText(viewerStrings.webComponentsFieldsDialog.findAField2)}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {hasText(viewerStrings.webComponentsFieldsDialog.selectAll) ? (
            <button type="button" onClick={() => state.selectAll()}>
              {viewerStrings.webComponentsFieldsDialog.selectAll}
            </button>
          ) : null}
          {hasText(viewerStrings.webComponentsFieldsDialog.unselectAll) ? (
            <button type="button" onClick={() => state.unselectAll()}>
              {viewerStrings.webComponentsFieldsDialog.unselectAll}
            </button>
          ) : null}
        </div>
        <div className="field-list">
          {snapshot.available
            .filter((field) => field.title.toLowerCase().includes(search.toLowerCase()))
            .map((field) => (
              <Checkbox
                key={field.id}
                checked={selected.has(field.id)}
                onChange={({ detail }) => state.setVisible(field.id, detail.checked)}
              >
                {field.title}
              </Checkbox>
            ))}
        </div>
      </Modal>
    </>
  );
}
